import { XLSX_MEDIA_TYPE } from '@/utils/fileDownload';

/**
 * Which files the Upload step takes: one `.xlsx` workbook. Pure, so the
 * rules are tested without the DOM; the step's hook calls it for both the
 * drop and the file picker.
 */

/** Why a pick was turned down; the View puts it into words. */
export type FileRefusal =
    { kind: 'notXlsx'; fileName: string } | { kind: 'severalFiles' };

export type FileIntake =
    | { kind: 'none' }
    | { kind: 'accepted'; file: File }
    | { kind: 'refused'; refusal: FileRefusal };

/** The file input's filter: the extension, and the media type for pickers that go by it. */
export const XLSX_ACCEPT = `.xlsx,${XLSX_MEDIA_TYPE}`;

/**
 * A workbook by its extension or its media type. A dragged file often comes
 * without a media type, so the name alone is enough.
 */
export const isXlsxFile = (file: Pick<File, 'name' | 'type'>): boolean =>
    file.type === XLSX_MEDIA_TYPE || file.name.toLowerCase().endsWith('.xlsx');

/**
 * One file in, one import: several files at once are refused rather than
 * guessed between, and anything but a workbook is refused by name.
 */
export const intakeOf = (files: readonly File[]): FileIntake => {
    const [file] = files;
    if (!file) {
        return { kind: 'none' };
    }
    if (files.length > 1) {
        return { kind: 'refused', refusal: { kind: 'severalFiles' } };
    }
    if (!isXlsxFile(file)) {
        return {
            kind: 'refused',
            refusal: { kind: 'notXlsx', fileName: file.name },
        };
    }
    return { kind: 'accepted', file };
};

/** What a drop hands over: file items resolve lazily through `getFile()`. */
export type DroppedItem = { kind: string; getFile?: () => Promise<File> };

/** The files of a drop, in order; text and directory items are left out. */
export const droppedFiles = async (
    items: readonly DroppedItem[],
): Promise<File[]> => {
    const files: File[] = [];
    for (const item of items) {
        if (item.kind === 'file' && item.getFile) {
            files.push(await item.getFile());
        }
    }
    return files;
};
